import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Search } from 'lucide-react';
import { citiesData } from '../mockData';

export const CitiesRates = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCities = citiesData.filter(city =>
    city.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">Cities & Rates</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Transparent, flat-rate pricing for all Chicago North & Northwest Suburbs.
            No hidden fees, no surprises.
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search Bar */}
          <div className="mb-12 max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Search for your city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 py-6 text-lg border-2 focus:border-[#00D4FF] transition-colors"
              />
            </div>
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-black text-white">
                  <th className="px-6 py-4 text-left text-lg font-semibold">City</th>
                  <th className="px-6 py-4 text-center text-lg font-semibold">Sedan (up to 3 passengers)</th>
                  <th className="px-6 py-4 text-center text-lg font-semibold">SUV (up to 6 passengers)</th>
                </tr>
              </thead>
              <tbody>
                {filteredCities.map((city, index) => (
                  <tr
                    key={index}
                    className={`border-b transition-colors hover:bg-gray-50 ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                    }`}
                  >
                    <td className="px-6 py-4 font-medium text-gray-900">{city.name}</td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-2xl font-bold text-[#00D4FF]">${city.sedan}</span>
                      <div className="text-xs text-gray-500 mt-1">+ 20% gratuity</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-2xl font-bold text-[#00D4FF]">${city.suv}</span>
                      <div className="text-xs text-gray-500 mt-1">+ 20% gratuity</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {filteredCities.map((city, index) => (
              <Card key={index} className="shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">{city.name}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-600 mb-1">Sedan</div>
                      <div className="text-3xl font-bold text-[#00D4FF]">${city.sedan}</div>
                      <div className="text-xs text-gray-500 mt-1">up to 3 passengers</div>
                      <div className="text-xs text-gray-500 mt-1">+ 20% gratuity</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-600 mb-1">SUV</div>
                      <div className="text-3xl font-bold text-[#00D4FF]">${city.suv}</div>
                      <div className="text-xs text-gray-500 mt-1">up to 6 passengers</div>
                      <div className="text-xs text-gray-500 mt-1">+ 20% gratuity</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredCities.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No cities found matching your search.</p>
            </div>
          )}
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Pricing Details</h2>
          <div className="space-y-6 text-gray-700">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-semibold text-lg mb-2 text-gray-900">What's Included</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#00D4FF] rounded-full mr-3 mt-2" />
                  <span>All taxes and fees</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#00D4FF] rounded-full mr-3 mt-2" />
                  <span>Fuel and toll charges</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#00D4FF] rounded-full mr-3 mt-2" />
                  <span>Professional chauffeur</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#00D4FF] rounded-full mr-3 mt-2" />
                  <span>Clean and comfortable vehicles</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-semibold text-lg mb-2 text-gray-900">Payment Options</h3>
              <p>We accept all major credit cards and debit cards.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-semibold text-lg mb-2 text-gray-900">Cancellation Policy</h3>
              <p>Free cancellation up to 3 hours before scheduled pickup. Contact us for details.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-semibold text-lg mb-2 text-gray-900">Airport Pickup Rules</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#00D4FF] rounded-full mr-3 mt-2" />
                  <span>30 minutes complimentary waiting (Domestic Flights)</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#00D4FF] rounded-full mr-3 mt-2" />
                  <span>45 minutes complimentary waiting (International Flights)</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#00D4FF] rounded-full mr-3 mt-2" />
                  <span>Flight Tracking included</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#00D4FF] rounded-full mr-3 mt-2" />
                  <span>After grace period billed in 15-minute intervals</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#00D4FF] rounded-full mr-3 mt-2" />
                  <span>Between 11pm - 5am additional charges will apply</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};